import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Check, X, MessageSquareText } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Avatar from '@/components/ui/Avatar';
import EmptyState from '@/components/ui/EmptyState';
import Toolbar from '@/components/ui/Toolbar';

const STATUS_FILTER = (t) => [
  { value: '', label: t('reviewsAdmin.filter.all') },
  { value: 'pending', label: t('reviewsAdmin.filter.pending') },
  { value: 'approved', label: t('reviewsAdmin.filter.approved') },
  { value: 'rejected', label: t('reviewsAdmin.filter.rejected') },
];

const STATUS_BADGE = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
};

/** Estrelas preenchidas conforme rating (0–5). */
function Stars({ rating = 0 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }} aria-label={`${rating}/5`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= Math.round(rating);
        return (
          <Star
            key={n}
            size={14}
            strokeWidth={2}
            fill={on ? 'var(--warning)' : 'none'}
            color={on ? 'var(--warning)' : 'var(--low)'}
          />
        );
      })}
    </span>
  );
}

export default function AdminReviews() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get('/reviews/admin', status ? { status } : {})
      .then((res) => setRows((res.data || res).reviews || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [status]);

  const moderate = async (id, next) => {
    setBusy(id + next);
    try {
      const res = await api.patch(`/reviews/${id}/moderate`, { status: next });
      const updated = (res.data || res).review;
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...(updated || { status: next }) } : r))
      );
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  const targetLabel = (r) => {
    const key = r.targetType === 'package' ? 'reviewsAdmin.target.package' : 'reviewsAdmin.target.trainer';
    return t(key, { id: r.targetId });
  };

  const columns = [
    {
      key: 'author',
      header: t('reviewsAdmin.column.author'),
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={r.author?.name} src={r.author?.avatar} size={34} />
          <span style={{ fontWeight: 600 }}>{r.author?.name || '—'}</span>
        </div>
      ),
    },
    {
      key: 'target',
      header: t('reviewsAdmin.column.target'),
      render: (r) => <span style={{ color: 'var(--mid)' }}>{targetLabel(r)}</span>,
    },
    {
      key: 'rating',
      header: t('reviewsAdmin.column.rating'),
      render: (r) => <Stars rating={r.rating} />,
    },
    {
      key: 'comment',
      header: t('reviewsAdmin.column.comment'),
      render: (r) => (
        <span style={{ color: 'var(--mid)', display: 'block', maxWidth: 360 }}>
          {r.comment || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('screens.status'),
      render: (r) => (
        <Badge variant={STATUS_BADGE[r.status] || 'neutral'}>
          {t(`reviewsAdmin.status.${r.status}`, r.status)}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: t('reviewsAdmin.column.date'),
      render: (r) =>
        r.createdAt ? (
          <span style={{ color: 'var(--low)', fontSize: 12 }}>
            {new Date(r.createdAt).toLocaleDateString()}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {r.status !== 'approved' && (
            <Button
              size="sm"
              variant="outline"
              icon={Check}
              loading={busy === r.id + 'approved'}
              onClick={() => moderate(r.id, 'approved')}
            >
              {t('reviewsAdmin.action.approve')}
            </Button>
          )}
          {r.status !== 'rejected' && (
            <Button
              size="sm"
              variant="danger"
              icon={X}
              loading={busy === r.id + 'rejected'}
              onClick={() => moderate(r.id, 'rejected')}
            >
              {t('reviewsAdmin.action.reject')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('reviewsAdmin.title')} />

      <Toolbar>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUS_FILTER(t)} />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        empty={
          <EmptyState icon={MessageSquareText} title={t('reviewsAdmin.empty')} />
        }
      />
    </>
  );
}
